// Termine als iCalendar-Datei (RFC 5545) - reine Logik, ohne Firebase und
// ohne DOM, dadurch aus Jest direkt testbar. Das Herunterladen macht
// components/shared/AddToCalendarButton.js.

import { toDate } from './dateUtils';
import { isAllDay } from './eventTime';

/** Ohne Ende-Angabe: zwei Stunden. Siehe buildEventIcs. */
export const DEFAULT_DURATION_MINUTES = 120;

/**
 * Laenge eines Zeichens in UTF-8-Oktetten.
 *
 * Selbst gerechnet statt ueber TextEncoder: Die Faltung unten zaehlt laut
 * Norm Oktette, nicht Zeichen - und ein "ü" ist zwei davon. Ohne das faltet
 * ein deutscher Text zu spaet und die Zeile wird zu lang.
 */
function utf8Length(char) {
  const code = char.codePointAt(0);
  if (code <= 0x7f) return 1;
  if (code <= 0x7ff) return 2;
  if (code <= 0xffff) return 3;
  return 4;
}

/**
 * Sonderzeichen in einem Textfeld maskieren.
 *
 * Reihenfolge ist wichtig: Der Backslash zuerst, sonst verdoppelt der
 * Durchgang danach die gerade erst eingefuegten Backslashes. Ohne das
 * Maskieren zerlegte ein Treffpunkt wie "Circuit Meppen, Halle 3" das Feld
 * am Komma in zwei Werte.
 */
export function escapeIcsText(value) {
  return String(value ?? '')
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\r\n|\r|\n/g, '\\n');
}

/**
 * Zeilen auf 75 Oktette falten (RFC 5545, Abschnitt 3.1).
 *
 * Fortsetzungszeilen beginnen mit einem Leerzeichen, das mitzaehlt - deshalb
 * bleiben dort nur 74 Oktette fuer den Inhalt. Manche Kalender sind hier
 * grosszuegig, Outlook ist es nicht.
 */
export function foldIcsLine(line) {
  const parts = [];
  let current = '';
  let bytes = 0;

  for (const char of String(line)) {
    const size = utf8Length(char);
    const limit = parts.length === 0 ? 75 : 74;
    if (bytes + size > limit) {
      parts.push(current);
      current = '';
      bytes = 0;
    }
    current += char;
    bytes += size;
  }
  parts.push(current);

  return parts[0] + parts.slice(1).map((part) => `\r\n ${part}`).join('');
}

/**
 * Zeitpunkt als UTC-Stempel: 20261003T090000Z.
 *
 * Bewusst UTC statt einer Zeitzonen-Angabe mit VTIMEZONE-Block: Ein
 * Firestore-Timestamp ist ein absoluter Zeitpunkt, UTC gibt ihn exakt wieder,
 * und der Kalender des Empfaengers rechnet selbst in seine Zone um. Der
 * VTIMEZONE-Weg braeuchte die vollstaendigen Sommerzeit-Regeln in der Datei -
 * viel mehr Text und eine zusaetzliche Fehlerquelle fuer denselben Zeitpunkt.
 */
export function toIcsUtc(value) {
  const date = toDate(value);
  if (!date) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return (
    `${date.getUTCFullYear()}${pad(date.getUTCMonth() + 1)}${pad(date.getUTCDate())}` +
    `T${pad(date.getUTCHours())}${pad(date.getUTCMinutes())}${pad(date.getUTCSeconds())}Z`
  );
}

/**
 * Ein Tag als reines Datum: 20261024 - fuer ganztaegige Termine.
 *
 * Bewusst LOKAL gelesen und nicht in UTC: Ein Termin ohne Uhrzeit steht auf
 * 00:00 Ortszeit, und in UTC waere das der Vortag um 22 oder 23 Uhr - der
 * Kalender zeigte den Termin also einen Tag zu frueh.
 */
export function toIcsDate(value) {
  const date = toDate(value);
  if (!date) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(date.getDate())}`;
}

/** Der Folgetag - fuer DTEND, das bei ganztaegigen Terminen EXKLUSIV ist. */
function nextDay(value) {
  const date = toDate(value);
  if (!date) return null;
  const next = new Date(date);
  next.setDate(next.getDate() + 1);
  return next;
}

/** Dateiname aus dem Titel - ohne Sonderzeichen, die Betriebssysteme stoeren. */
export function icsFileName(title) {
  const slug = String(title ?? '')
    .toLowerCase()
    .replace(/[äöüß]/g, (c) => ({ ä: 'ae', ö: 'oe', ü: 'ue', ß: 'ss' })[c])
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);
  return `${slug || 'termin'}.ics`;
}

/**
 * Einen Termin als vollstaendige .ics-Datei.
 *
 * @param {object} event   Termin aus Firestore (title, startAt, endAt, location, description, link)
 * @param {object} options origin = Adresse der Seite (fuer UID und Detail-Link),
 *                         now = Zeitpunkt fuer DTSTAMP (im Test setzbar)
 * @returns {string|null}  null, wenn der Termin keinen gueltigen Beginn hat
 */
export function buildEventIcs(event, { origin = '', now = new Date() } = {}) {
  const start = toDate(event?.startAt);
  if (!start) return null;

  // Ohne Ende zwei Stunden ansetzen: Ein Termin ohne DTEND ist laut Norm
  // zwar erlaubt, manche Kalender zeigen ihn dann aber als Zeitpunkt ohne
  // Dauer an - im Wochenraster ein kaum sichtbarer Strich.
  const end =
    toDate(event?.endAt) ?? new Date(start.getTime() + DEFAULT_DURATION_MINUTES * 60000);

  // Ein Termin ohne Uhrzeit (v0.20.2) ist im Kalender ein GANZTAEGIGER, kein
  // zweistuendiger um Mitternacht. Die Norm verlangt dafuer den Datumstyp -
  // und ein DTEND, das den ersten Tag NACH dem Termin nennt.
  const allDay = isAllDay(event);
  const startLine = allDay
    ? `DTSTART;VALUE=DATE:${toIcsDate(start)}`
    : `DTSTART:${toIcsUtc(start)}`;
  const endLine = allDay
    ? `DTEND;VALUE=DATE:${toIcsDate(nextDay(toDate(event?.endAt) ?? start))}`
    : `DTEND:${toIcsUtc(end)}`;

  const host = String(origin).replace(/^https?:\/\//, '') || 'clubminithrills.web.app';
  const detailUrl = event?.id && origin ? `${origin}/intern/termine/${event.id}` : '';

  // Beschreibung, Link und die Adresse der Termindetails in EIN Feld: Mehr
  // als eine DESCRIPTION ist nicht erlaubt.
  const description = [event?.description, event?.link, detailUrl]
    .filter(Boolean)
    .join('\n\n');

  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//minithrills//Terminkalender//DE',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    // Stabile UID aus der Firestore-ID: Wer denselben Termin ein zweites Mal
    // importiert, aktualisiert damit den vorhandenen Eintrag, statt einen
    // Doppelgaenger im Kalender zu erzeugen.
    `UID:${event?.id || `${toIcsUtc(start)}-${Math.random().toString(36).slice(2)}`}@${host}`,
    `DTSTAMP:${toIcsUtc(now)}`,
    startLine,
    endLine,
    `SUMMARY:${escapeIcsText(event?.title || 'Termin')}`,
  ];

  if (event?.location) lines.push(`LOCATION:${escapeIcsText(event.location)}`);
  if (description) lines.push(`DESCRIPTION:${escapeIcsText(description)}`);
  if (event?.link) lines.push(`URL:${escapeIcsText(event.link)}`);

  lines.push('END:VEVENT', 'END:VCALENDAR');

  // CRLF ist in RFC 5545 vorgeschrieben. Viele Kalender verzeihen ein blosses
  // \n, Outlook nicht - und ein Import, der nur bei manchen scheitert, faellt
  // beim Testen am eigenen Rechner nicht auf.
  return lines.map(foldIcsLine).join('\r\n') + '\r\n';
}
